import { useMutation } from '@provision/graphql-client';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { partition } from 'remeda';
import { DATE_TIME_FORMAT } from 'src/constants/date';
import {
  PartnerHolidayEntity,
  PartnerHolidayMutationResponse,
  PartnerHolidayQueryResponse,
} from 'src/graphql/entities/partner-holiday/partner-holiday.types';
import {
  GET_PARTNER_HOLIDAY_QUERY,
  UPDATE_PARTNER_HOLIDAY_QUERY,
} from 'src/graphql/entities/partner-holiday/partner-holidays.operations';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { ConditionInput, QueryVariables } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { GetPartnerHolidayQuery } from './use-partner-holiday';

export interface UpdatePartnerHolidayMutation {
  updatePartnerHoliday: PartnerHolidayMutationResponse[];
}

export type UpdatePartnerHolidayMutationParams = {
  conditions: ConditionInput[];
  data: Omit<PartnerHolidayMutationResponse, 'id' | 'partnerHolidayId' | 'partnerId'>;
};

export const useUpdatePartnerHoliday = () => {
  const { user } = useCurrentUserProfile();
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const today = useMemo(() => dayjs().startOf('day').format(DATE_TIME_FORMAT), []);

  const [updatePartnerHolidayMutation, { loading: isUpdating }] = useMutation<
    UpdatePartnerHolidayMutation,
    UpdatePartnerHolidayMutationParams
  >(UPDATE_PARTNER_HOLIDAY_QUERY, {
    update(cache, { data }) {
      if (!data?.updatePartnerHoliday?.length) {
        return;
      }

      const [upcomingHolidays, pastHolidays] = partition(data.updatePartnerHoliday, (value) =>
        dayjs(value.holidayEnd).isSameOrAfter(today),
      );

      cache.updateQuery<GetPartnerHolidayQuery, QueryVariables>(
        {
          query: GET_PARTNER_HOLIDAY_QUERY,
          variables: {
            params: {
              filters: [
                {
                  attribute: 'partnerId',
                  operator: '=',
                  value: partnerId?.toString()!,
                },
                {
                  attribute: 'holidayEnd',
                  operator: '>=',
                  value: today,
                },
              ],
            },
          },
        },
        updateCacheFn(upcomingHolidays, pastHolidays, user),
      );
      cache.updateQuery<GetPartnerHolidayQuery, QueryVariables>(
        {
          query: GET_PARTNER_HOLIDAY_QUERY,
          variables: {
            params: {
              filters: [
                {
                  attribute: 'partnerId',
                  operator: '=',
                  value: partnerId?.toString()!,
                },
                {
                  attribute: 'holidayEnd',
                  operator: '<',
                  value: today,
                },
              ],
            },
          },
        },
        updateCacheFn(pastHolidays, upcomingHolidays, user),
      );
    },
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to create holiday entity, please try again');
    },
    onCompleted: (data) => {
      if (data.updatePartnerHoliday?.[0].holidayName) {
        showSuccessAlert(`${data.updatePartnerHoliday[0].holidayName}  holiday updated!`, {
          autoHide: 2000,
        });
      }
    },
  });

  const updatePartnerHoliday = useCallback(
    async (id: PartnerHolidayEntity['id'], value: Omit<PartnerHolidayEntity, 'id'>) => {
      if (!user?.userId) {
        console.error('Cannot create holiday entity. Current user is not found');
        return;
      }
      await updatePartnerHolidayMutation({
        variables: {
          conditions: [
            {
              attribute: 'id',
              operator: '=',
              value: String(id),
            },
          ],
          data: {
            holidayName: value.holidayName,
            submitterId: user.userId,
            holidayStart: value.holidayStart.format(DATE_TIME_FORMAT),
            holidayEnd: value.holidayEnd.format(DATE_TIME_FORMAT),
          },
        },
        optimisticResponse: () => ({
          updatePartnerHoliday: [
            {
              __typename: 'PartnerHoliday',
              id,
              holidayName: value.holidayName,
              holidayStart: value.holidayStart.format(DATE_TIME_FORMAT),
              holidayEnd: value.holidayEnd.format(DATE_TIME_FORMAT),
              partnerHolidayId: id,
              partnerId: value.partnerId,
              submitterId: value.submitterId,
            },
          ],
        }),
      });
    },
    [updatePartnerHolidayMutation, user?.userId],
  );

  return {
    updatePartnerHoliday,
    isUpdating,
  };
};

function updateCacheFn(
  newRecords: PartnerHolidayMutationResponse[] | undefined = [],
  deleteRecords: PartnerHolidayMutationResponse[] = [],
  user: PartnerHolidayQueryResponse['platformUser'] | null,
) {
  return (cached: GetPartnerHolidayQuery | null) => {
    const records = (cached?.getPartnerHoliday?.records || []).filter(
      (record) => ![...deleteRecords, ...newRecords].some((r) => r.id === record.id),
    );

    const updatedRecords = newRecords.map((record) => {
      const cachedValue = cached?.getPartnerHoliday?.records?.find(
        (cachedRecord) => cachedRecord.id === record.id,
      );

      return {
        ...(cachedValue || {}),
        ...record,
        platformUser: {
          ...cachedValue?.platformUser,
          id: user?.id || '',
          userId: user?.userId || '',
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
        },
      };
    });

    return {
      getPartnerHoliday: {
        __typename: 'PartnerHolidayDataResponse',
        records: [...records, ...updatedRecords],
        metadata: cached?.getPartnerHoliday.metadata ?? {},
      },
    };
  };
}
